import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import useToggle from 'dpl/shared/hooks/useToggle';

const DEFAULT_BUTTON_LABELS = {
  expanded: 'See less',
  collapsed: 'See more'
};

const DEFAULT_MAX_COLLAPSED_COUNT = 6;

export default function CollapsibleList({
  id,
  className,
  buttonClassName,
  isCollapsed: initialIsCollapsed,
  items: initialItems,
  maxCollapsedCount,
  buttonLabels
}) {
  const [isCollapsed, toggleIsCollapsed] = useToggle(initialIsCollapsed);
  const isCollapsible = initialItems.length > maxCollapsedCount;

  const { items, buttonAriaLabel, buttonLabel } = useMemo(() => {
    if (isCollapsed) {
      const collapsedItems = initialItems.slice(0, maxCollapsedCount);
      const hiddenItemsCount = initialItems.length - collapsedItems.length;

      return {
        items: collapsedItems,
        buttonAriaLabel: `Show ${hiddenItemsCount} more ${hiddenItemsCount === 1 ? 'item' : 'items'}`,
        buttonLabel: buttonLabels.collapsed
      };
    }

    return {
      items: initialItems,
      buttonAriaLabel: 'Show fewer items',
      buttonLabel: buttonLabels.expanded
    };
  }, [isCollapsed]);

  return (
    <div
      className={classnames(
        'CollapsibleList font-16 md:font-18 secondary',
        className
      )}
    >
      <ul id={id} aria-live="polite">
        {items.map((item, index) => (
          <li
            key={index}
            className={classnames({
              'bt b--default pt3 pt4-md': index > 0,
              'pb3 pb4-md': index < items.length - 1
            })}
          >
            {item}
          </li>
        ))}
      </ul>
      {isCollapsible && (
        <button
          type="button"
          className={classnames(
            'fw-medium stone-700 underline',
            buttonClassName
          )}
          onClick={toggleIsCollapsed}
          aria-expanded={!isCollapsed}
          aria-controls={id}
          aria-label={buttonAriaLabel}
        >
          {buttonLabel}
        </button>
      )}
    </div>
  );
}

CollapsibleList.propTypes = {
  id: PropTypes.string.isRequired,
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  isCollapsed: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.node])
  ).isRequired,
  maxCollapsedCount: PropTypes.number,
  buttonLabels: PropTypes.shape({
    expanded: PropTypes.string.isRequired,
    collapsed: PropTypes.string.isRequired
  })
};

CollapsibleList.defaultProps = {
  className: null,
  buttonClassName: 'mt4',
  isCollapsed: !window.__DPL_BOT_UA,
  maxCollapsedCount: DEFAULT_MAX_COLLAPSED_COUNT,
  buttonLabels: DEFAULT_BUTTON_LABELS
};
